import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { authUtils } from "@/utils/auth";
import {
  Crown, Star, Zap, Clock, Users, RefreshCw, XCircle, CheckCircle,
  AlertCircle, ChevronDown
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_ICONS = {
  silver: Star,
  bronze: Zap,
  gold: Crown
};

const TIER_COLORS = {
  silver: "bg-gray-500",
  bronze: "bg-amber-600",
  gold: "bg-yellow-500"
};

const STATUS_STYLES = {
  active: { bg: "bg-green-500/20", text: "text-green-400", icon: CheckCircle },
  cancelled: { bg: "bg-red-500/20", text: "text-red-400", icon: XCircle },
  past_due: { bg: "bg-yellow-500/20", text: "text-yellow-400", icon: AlertCircle },
  paused: { bg: "bg-gray-500/20", text: "text-gray-400", icon: Clock }
};

export default function MembershipManagement() {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchMemberships = async () => {
    setLoading(true);
    try {
      const token = authUtils.getToken();
      const response = await axios.get(`${API}/admin/memberships`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMemberships(response.data.memberships || []);
    } catch (error) {
      console.error("Error fetching memberships:", error);
      toast.error("Failed to load memberships");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMemberships();
  }, []);

  const handleCancel = async (membershipId, customerName) => {
    const reason = window.prompt(`Cancel ${customerName}'s membership?\n\nEnter cancellation reason (optional):`);
    if (reason === null) return; // User clicked Cancel

    setCancellingId(membershipId);
    try {
      const token = authUtils.getToken();
      await axios.post(
        `${API}/admin/memberships/${membershipId}/cancel`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { reason: reason || "Cancelled by admin" }
        }
      );
      toast.success("Membership cancelled successfully");
      fetchMemberships();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel membership");
    } finally {
      setCancellingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Stats
  const stats = {
    total: memberships.length,
    active: memberships.filter(m => m.status === 'active').length,
    totalHoursUsed: memberships.reduce((sum, m) => sum + (m.hours_used_this_period || 0), 0),
    monthlyRevenue: memberships
      .filter(m => m.status === 'active')
      .reduce((sum, m) => sum + (m.tier_details?.price || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-dream-violet animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass rounded-xl p-4">
          <p className="text-white/50 text-sm">Total Members</p>
          <p className="text-2xl font-bold text-white">{stats.total}</p>
        </div>
        <div className="glass rounded-xl p-4">
          <p className="text-white/50 text-sm">Active</p>
          <p className="text-2xl font-bold text-green-400">{stats.active}</p>
        </div>
        <div className="glass rounded-xl p-4">
          <p className="text-white/50 text-sm">Hours Used (This Period)</p>
          <p className="text-2xl font-bold text-dream-violet">{stats.totalHoursUsed}</p>
        </div>
        <div className="glass rounded-xl p-4">
          <p className="text-white/50 text-sm">Monthly Revenue</p>
          <p className="text-2xl font-bold text-white">${stats.monthlyRevenue}</p>
        </div>
      </div>

      {/* Memberships List */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading text-xl font-bold text-white">All Memberships</h2>
            <p className="text-white/50 text-sm">Manage production room members</p>
          </div>
          <Button onClick={fetchMemberships} variant="outline" className="border-dream-border text-white">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {memberships.length === 0 ? (
          <div className="text-center py-12">
            <Crown className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/50">No memberships yet</p>
            <p className="text-white/30 text-sm">Members will appear here once they subscribe</p>
          </div>
        ) : (
          <div className="space-y-4">
            {memberships.map((membership) => {
              const TierIcon = TIER_ICONS[membership.tier] || Star;
              const tierColor = TIER_COLORS[membership.tier] || "bg-purple-500";
              const statusStyle = STATUS_STYLES[membership.status] || STATUS_STYLES.active;
              const StatusIcon = statusStyle.icon;
              const isExpanded = expandedId === membership.id;

              return (
                <div key={membership.id} className="bg-dream-surface rounded-xl overflow-hidden">
                  {/* Main Row */}
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5"
                    onClick={() => setExpandedId(isExpanded ? null : membership.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg ${tierColor} flex items-center justify-center`}>
                        <TierIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{membership.customer_name}</p>
                        <p className="text-white/50 text-sm">{membership.customer_email}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right hidden sm:block">
                        <p className="text-white font-mono capitalize">{membership.tier}</p>
                        <p className="text-white/50 text-sm">
                          {membership.hours_remaining}/{membership.tier_details?.hours || 0} hrs
                        </p>
                      </div>

                      <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${statusStyle.bg}`}>
                        <StatusIcon className={`w-4 h-4 ${statusStyle.text}`} />
                        <span className={`text-sm capitalize ${statusStyle.text}`}>{membership.status}</span>
                      </div>

                      <ChevronDown className={`w-5 h-5 text-white/50 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-dream-border pt-4">
                      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-white/50 text-xs">Tier</p>
                          <p className="text-white capitalize">{membership.tier} - ${membership.tier_details?.price}/mo</p>
                        </div>
                        <div>
                          <p className="text-white/50 text-xs">Hours This Period</p>
                          <p className="text-white">
                            <span className="text-dream-violet">{membership.hours_remaining}</span> remaining / {membership.hours_used_this_period} used
                          </p>
                        </div>
                        <div>
                          <p className="text-white/50 text-xs">Billing Cycle Start</p>
                          <p className="text-white">{formatDate(membership.billing_cycle_start)}</p>
                        </div>
                        <div>
                          <p className="text-white/50 text-xs">Next Billing</p>
                          <p className="text-white">{formatDate(membership.next_billing_date)}</p>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-white/50 text-xs">Phone</p>
                          <p className="text-white">{membership.customer_phone || "-"}</p>
                        </div>
                        <div>
                          <p className="text-white/50 text-xs">Member Since</p>
                          <p className="text-white">{formatDate(membership.created_at)}</p>
                        </div>
                        {membership.cancelled_at && (
                          <>
                            <div>
                              <p className="text-white/50 text-xs">Cancelled On</p>
                              <p className="text-red-400">{formatDate(membership.cancelled_at)}</p>
                            </div>
                            <div>
                              <p className="text-white/50 text-xs">Cancel Reason</p>
                              <p className="text-white">{membership.cancel_reason || "-"}</p>
                            </div>
                          </>
                        )}
                      </div>

                      {membership.status === 'active' && (
                        <div className="flex justify-end">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancel(membership.id, membership.customer_name);
                            }}
                            disabled={cancellingId === membership.id}
                            variant="outline"
                            className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                          >
                            {cancellingId === membership.id ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Cancelling...
                              </>
                            ) : (
                              <>
                                <XCircle className="w-4 h-4 mr-2" />
                                Cancel Membership
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
